import type { User } from "@supabase/supabase-js";
import type { Href } from "expo-router";

import { supabase } from "./supabase";

export type AppRole =
  | "student"
  | "client"
  | "manager"
  | "judge"
  | "campus_admin"
  | "admin_licensing"
  | "admin_rm"
  | "super_admin";

export type SignUpPortalRole = "student" | "client";

function isAppRole(value: string): value is AppRole {
  return (
    value === "student" ||
    value === "client" ||
    value === "manager" ||
    value === "judge" ||
    value === "campus_admin" ||
    value === "admin_licensing" ||
    value === "admin_rm" ||
    value === "super_admin"
  );
}

export async function fetchRoleForUserId(userId: string): Promise<AppRole | null> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data?.role || typeof data.role !== "string") return null;
  return isAppRole(data.role) ? data.role : null;
}

export async function signUpWithEmail(
  email: string,
  password: string,
  fullName: string,
  role: SignUpPortalRole
) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { full_name: fullName },
    },
  });

  if (error) return { data: null, error };

  if (data.user) {
    const { error: roleError } = await supabase.from("user_roles").insert({
      user_id: data.user.id,
      role,
    });
    if (roleError) {
      return { data, error: roleError };
    }
    // Fire-and-forget — analytics is graceful no-op when key missing.
    // Lazy-imported to dodge a circular type dependency through
    // authContext when this module is bundled.
    void import("./analytics").then(({ track }) => track("sign_up_completed", { role }));
  }

  return { data, error: null };
}

export async function signInWithEmail(email: string, password: string) {
  const result = await supabase.auth.signInWithPassword({ email, password });
  if (!result.error && result.data.user) {
    void import("./analytics").then(({ track }) => track("sign_in_completed"));
  }
  return result;
}

export async function signOut() {
  return supabase.auth.signOut();
}

/**
 * User-initiated sign out. Wraps `signOut` with two extra concerns:
 *
 *  1. Clears the `coravu_intro_seen_v3` sessionStorage flag (web only) so the
 *     marketing intro animation plays again when the user lands on `/`. This
 *     gives sign-out a deliberate, branded transition instead of dropping the
 *     person directly onto the bare login page.
 *  2. Replaces the route to `/` (the marketing landing) instead of `/login`.
 *     The landing page already redirects authenticated users to their portal,
 *     so this is safe; an unauthenticated user lands on the marketing site
 *     where they can re-enter via the SIGN IN CTA.
 *
 * Use this for any button labelled "Sign out" or "Sign out all sessions".
 * For system-initiated redirects (gates kicking out an unauthenticated user)
 * keep using `router.replace("/login")` directly.
 */
export async function signOutToHome(router: { replace: (href: Href) => void }): Promise<void> {
  if (typeof window !== "undefined") {
    try {
      // PortalGate suppresses /login redirects while this flag is set.
      window.sessionStorage?.setItem("coravu_signing_out", "1");
      window.sessionStorage?.removeItem("coravu_intro_seen_v3");
      // Also reset the brand-entrance flag so the next sign-in plays the
      // entry animation again — a fresh session deserves the full intro.
      window.sessionStorage?.removeItem("coravu_brand_entrance_played");
    } catch {
      // sessionStorage may be disabled (privacy mode); fall through.
    }
  }

  // Await signOut so the supabase session is FULLY cleared from
  // localStorage before we navigate. If we don\'t await, the new page
  // load sees a stale session and the index route falls through to
  // /login. PortalGate sees the signing-out flag and stays put while
  // we await.
  try {
    await supabase.auth.signOut();
  } catch {
    // ignore — we still navigate away
  }

  if (typeof window !== "undefined") {
    try {
      window.location.replace("/");
      return;
    } catch {
      // fall through to router.replace below
    }
  }
  router.replace("/" as Href);
}

export async function getCurrentUserAndRole(): Promise<{ user: User; role: AppRole } | null> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) return null;

  const role = await fetchRoleForUserId(userData.user.id);
  if (!role) return null;

  return { user: userData.user, role };
}

/** Session + role + admin profile fields used right after sign-in (MFA / onboarding routing). */
export type LoginProfile = {
  user: User;
  role: AppRole;
  employeeStatus: EmployeeStatus | null;
  mfaRequired: boolean;
};

/**
 * Defense-in-depth provisioning recovery.
 *
 * If a user signed up but didn't complete the signup form (e.g. they closed
 * the tab before clicking the email confirmation link, then came back via
 * /login a day later), the signup form's `complete_*_signup` RPC never fires
 * and the user lands with no `user_roles` row. This helper inspects
 * `user_metadata.coravu_role` (set by the signup forms) and re-fires the
 * appropriate RPC idempotently. Safe to call multiple times.
 *
 * Returns true if it called an RPC successfully (caller should re-load the
 * profile). Returns false if there was nothing to recover or recovery failed.
 */
export async function tryRecoverSignupProvisioning(): Promise<boolean> {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) return false;

  const md = (user.user_metadata ?? {}) as Record<string, unknown>;
  const coravuRole = typeof md.coravu_role === "string" ? md.coravu_role : null;

  if (coravuRole === "student") {
    const fullName = typeof md.full_name === "string" ? md.full_name.trim() : null;
    const university = typeof md.university === "string" ? md.university.trim() : null;
    const programLevel = typeof md.program_level === "string" ? md.program_level : null;
    const graduationTerm = typeof md.graduation_term === "string" ? md.graduation_term : null;
    const graduationYearRaw = md.graduation_year;
    const graduationYear =
      typeof graduationYearRaw === "number"
        ? graduationYearRaw
        : typeof graduationYearRaw === "string" && /^\d+$/.test(graduationYearRaw)
          ? parseInt(graduationYearRaw, 10)
          : null;
    const fieldsRaw = md.fields_of_competency;
    const fields = Array.isArray(fieldsRaw) ? (fieldsRaw.filter((x) => typeof x === "string") as string[]) : null;

    const { error } = await supabase.rpc("complete_student_signup", {
      p_full_name: fullName,
      p_university: university,
      p_major: null,
      p_graduation_year: graduationYear,
      p_year_in_school: null,
      p_program_level: programLevel,
      p_graduation_term: graduationTerm,
      p_fields_of_competency: fields,
    });
    if (error) return false;
    return true;
  }

  if (coravuRole === "client") {
    const companyName = typeof md.company_name === "string" ? md.company_name.trim() : null;
    if (!companyName) {
      // Without a company name we can't provision a client. Caller should
      // redirect to a "complete signup" screen rather than a useless portal.
      return false;
    }
    const companySize = typeof md.company_size === "string" ? md.company_size : null;
    const industry = typeof md.industry === "string" ? md.industry.trim() : null;

    const { error } = await supabase.rpc("complete_client_signup", {
      p_company_name: companyName,
      p_company_size: companySize,
      p_industry: industry || null,
    });
    if (error) return false;
    return true;
  }

  return false;
}

export async function loadLoginProfile(): Promise<LoginProfile | null> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) return null;
  const user = userData.user;
  const role = await fetchRoleForUserId(user.id);
  if (!role) return null;

  const { data } = await supabase.from("user_profiles").select("employee_status, mfa_required").eq("id", user.id).maybeSingle();
  const es = data?.employee_status;
  const employeeStatus: EmployeeStatus | null =
    es === "pending" || es === "active" || es === "rejected" ? es : null;
  // Admins: always required (treat missing/null/undefined as required for safety).
  // Everyone else: opt-in - they own the value via Settings -> Security.
  const mfaRequired = isAdminRole(role) ? data?.mfa_required !== false : data?.mfa_required === true;

  return { user, role, employeeStatus, mfaRequired };
}

/** Public path for each role portal (matches `app/(group)/<segment>/index.tsx`). */
export type PortalRoute = "/student" | "/client" | "/judge" | "/campus" | "/admin";

/**
 * Maps a role to the Expo Router path for that portal (public URL segment).
 *
 * `manager` is a retired role — the Manager portal was collapsed into a
 * reduced-permission Client (a "limited client"), so the legacy role now
 * routes into the Client portal. The `manager` enum value still exists in
 * the DB for historical rows; this mapping keeps any straggler functional.
 */
export function roleToRoute(role: AppRole): PortalRoute {
  switch (role) {
    case "student":
      return "/student";
    case "client":
    case "manager":
      return "/client";
    case "judge":
      return "/judge";
    case "campus_admin":
      return "/campus";
    case "admin_licensing":
    case "admin_rm":
    case "super_admin":
      return "/admin";
  }
}

export type PortalKey = "student" | "client" | "judge" | "campus" | "admin";

export type EmployeeStatus = "pending" | "active" | "rejected";

export function isAdminRole(role: AppRole | null | undefined): boolean {
  return role === "admin_licensing" || role === "admin_rm" || role === "super_admin";
}

export function portalAllowsRole(portal: PortalKey, role: AppRole): boolean {
  switch (portal) {
    case "student":
      return role === "student";
    case "client":
      // Limited clients (the former Manager role) live in the Client portal.
      // Accept the retired `manager` role defensively so any historical row
      // isn't locked out before the data migration converts it to `client`.
      return role === "client" || role === "manager";
    case "judge":
      return role === "judge";
    case "campus":
      return role === "campus_admin";
    case "admin":
      return role === "admin_licensing" || role === "admin_rm" || role === "super_admin";
  }
}
